import {
  getLastUserId,
  resetOfflineStorage,
  setLastUserId,
} from "@stockline/web";
import { setSentryUserContext } from "@/lib/observability/sentryTenant";
import { closeOfflineDatabase } from "@/lib/offline-db";
import api from "@/app/api";
import { UserResponse } from "@/app/api/types";
import { CompanyResponse } from "@/app/api/types/company";
import { Query, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@/hooks/use-router-compat";
import { usePathname } from "@/hooks/use-pathname-compat";
import { createContext, useContext, useEffect, useState } from "react";
import {
  clearSellerGroup,
  clearTenantGroup,
  identifyUser,
  pickPrimaryGroup,
  registerSuperProperties,
  resetAnalytics,
  setSellerGroup,
  setTenantGroup,
} from "@/lib/analytics";
import type { GroupResponse } from "@/app/api/types/group";
import type { EmployeeResponse } from "@/app/api/types/employee";
import { useMyGroups } from "@/query/hooks/useGroups";
import { employeeKeys, useEmployee } from "@/query/hooks/useEmployees";
import { featureFlagKeys } from "@/query/hooks/useFeatureFlags";
import { groupKeys } from "@/query/keys/groups";

// Mirror of setSentryUserContext for PostHog: identify the person, attach the
// company + seller group as PostHog groups, and register super properties so
// every event (pageview, autocapture, future custom events) is filterable by
// tenant + user + role + seller group without per-event instrumentation.
// `company`/`groups`/`employee` may each be missing while their query is
// still in flight — identify runs immediately and the effect re-fires as
// each piece settles, re-registering with the richer context.
function setPostHogUserContext(
  user: UserResponse | null | undefined,
  company: CompanyResponse | null | undefined,
  groups?: GroupResponse[],
  employee?: EmployeeResponse | null,
) {
  if (!user) {
    resetAnalytics();
    return;
  }
  identifyUser(user);
  const primaryGroup = pickPrimaryGroup(groups, user.id);
  // Only trust the employee record when it is the one linked to this user —
  // like groups, the employee cache can briefly hold another user's record
  // around an in-app user switch.
  const linkedEmployee =
    employee && employee.id === user.employee_id ? employee : null;
  registerSuperProperties({
    user_id: user.id,
    company_id: user.company_id,
    feature_permissions: user.feature_permissions,
    company_permissions: user.company_permissions,
    employee_id: user.employee_id,
    employee_code: linkedEmployee?.code ?? null,
    group_id: primaryGroup?.id ?? null,
  });
  // Same trust rule as groups/employee: `["company","me"]` is IDB-persisted,
  // so after a crash + relogin (no clean logout) it can hydrate with the
  // previous user's company for one render before the refetch/purge lands.
  const trustedCompany =
    company && company.id === user.company_id ? company : null;
  if (trustedCompany) {
    setTenantGroup(trustedCompany);
  } else if (company) {
    // The company query has SETTLED but its id doesn't match this user — a
    // stale ["company","me"] hydrate (crash + relogin serves the previous
    // user's company for one render). Drop any tenant group the PostHog store
    // still holds so the new identity's events aren't attributed to the
    // previous company. Gated on `company` being defined (not just untrusted)
    // so an in-flight fetch never flaps the group off and on. Runs before the
    // seller handling below because clearTenantGroup's resetGroups also wipes
    // the seller association, which that block then re-attaches.
    clearTenantGroup();
  }
  if (primaryGroup) {
    setSellerGroup(primaryGroup);
  } else if (groups !== undefined) {
    // The groups query has settled and this user has no seller group —
    // drop any association the PostHog store still holds (the SDK persists
    // groups and never unsets them on identify). Skipped while the query
    // is in flight so a slow fetch doesn't flap the group off and on.
    // Pass the trusted company so clearSellerGroup re-attaches the right
    // tenant (with full props) after resetGroups, instead of the possibly
    // stale key left in the PostHog store.
    clearSellerGroup(trustedCompany);
  }
}

// Shared by both providers: fetch the identity context observability needs
// (company for the tenant group, seller groups, linked employee for the
// seller code) and mirror it into Sentry + PostHog as each piece settles.
// Stale-while-revalidate is fine for all three — they change rarely.
function useObservabilityContext(user: UserResponse | null | undefined) {
  const { data: company } = useQuery({
    queryKey: ["company", "me"],
    queryFn: api.companies.getMe,
    enabled: Boolean(user?.company_id),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // Groups are tenant-scoped (`/groups/my-groups` runs on get_tenant_db), so
  // gate on company_id like the company query above — a company-less user
  // (e.g. a platform super admin) has no tenant and would otherwise trigger a
  // spurious 403.
  const { data: groups } = useMyGroups({
    enabled: Boolean(user?.company_id),
    staleTime: 5 * 60 * 1000,
  });

  const { data: employee } = useEmployee(user?.employee_id ?? null, {
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    // Only trust the company name when the company record belongs to this
    // user — the ["company","me"] query is IDB-persisted, so after a crash +
    // relogin it can hydrate the previous user's company for one render
    // (same trust rule as setPostHogUserContext below). Passing it blindly
    // would tag the new tenant's Sentry events with the previous tenant_name.
    const trustedCompanyName =
      company && company.id === user?.company_id ? company.name : undefined;
    setSentryUserContext(user, trustedCompanyName);
    setPostHogUserContext(user, company, groups, employee);
  }, [user, company, groups, employee]);
}

// Refetch interval based on permission level
// Level 0 users get faster updates to pick up permission upgrades quickly
const getUserRefetchInterval = (
  query: Query<UserResponse, Error, UserResponse, string[]>,
): number | false => {
  const userData = query.state.data;
  if (userData?.feature_permissions === 0) {
    return 30 * 1000; // 30 seconds for level 0 users
  }
  return 5 * 60 * 1000; // 5 minutes for others
};

const UserContext = createContext<UserResponse | null>(null);

export function useUser(): UserResponse {
  let res = useContext(UserContext);
  if (!res) throw new Error("User not logged in");
  return res;
}

export function useUserOptional(): UserResponse | null {
  return useContext(UserContext);
}

interface PermissionContextType {
  hasPermission: (feature: string) => boolean;
  hasAnyPermission: (features: string[]) => boolean;
  hasAllPermissions: (features: string[]) => boolean;
}

const PermissionContext = createContext<PermissionContextType>({
  hasPermission: () => false,
  hasAnyPermission: () => false,
  hasAllPermissions: () => false,
});

export function usePermissions() {
  return useContext(PermissionContext);
}

export function OptionalAuthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const queryClient = useQueryClient();
  const {
    data: user,
    isFetched,
    error,
  } = useQuery({
    queryKey: ["user"],
    queryFn: api.users.getMe,
    retry: false,
    refetchInterval: getUserRefetchInterval,
  });

  useObservabilityContext(user);

  // Clear stale user data from in-memory cache on auth error
  // Note: Don't clear persisted cache here - OptionalAuthProvider is used on
  // public pages like login, and clearing persisted cache would cause loops
  useEffect(() => {
    if (error) {
      queryClient.setQueryData(["user"], null);
      setSentryUserContext(null);
      setPostHogUserContext(null, null);
    }
  }, [error, queryClient]);

  // Return null (not stale user) when there's an auth error
  const effectiveUser = error ? null : user;
  const effectiveUserId = effectiveUser?.id ?? null;

  // Wipe local Dexie + legacy IDB if a different user logged in since the
  // last time this browser saw an authenticated session. We gate children
  // behind userSwitchReady so AISyncProvider (mounted downstream) doesn't
  // start streaming the new user's data into the previous user's tables.
  //
  // Important: a transient `getMe` background-refetch error flips
  // `effectiveUserId` from "user-x" → null → "user-x". We must NOT treat
  // those null transitions as a logout/user-switch — unmounting children
  // there would kill the SyncEngine and force a Dexie cold-restart on
  // every network hiccup. The actual wipe-and-remount only fires when we
  // see a brand-new non-null userId that differs from the persisted
  // `lastUserId`.
  const [userSwitchReady, setUserSwitchReady] = useState(false);
  useEffect(() => {
    if (!isFetched) return;
    // Null effectiveUserId means either:
    //   - first fetch settled with no user (login page) → mark ready so
    //     children render the unauthenticated tree
    //   - background refetch failure for an already-known user → keep
    //     existing readiness (and existing children) as-is
    if (effectiveUserId == null) {
      // No-op if already true (transient refetch error path); flips to
      // true on first fetch with no user (login page).
      setUserSwitchReady(true);
      return;
    }
    const previous = getLastUserId();
    if (previous === effectiveUserId) {
      // Same user we already saw — no wipe needed, just mark ready (a
      // no-op on subsequent renders since prev was already true).
      setUserSwitchReady(true);
      return;
    }
    // Genuine user switch on this browser. Tear children down while we
    // wipe so AISyncProvider doesn't stream the new user's data into the
    // previous user's tables.
    setUserSwitchReady(false);
    let cancelled = false;
    void (async () => {
      if (previous != null) {
        try {
          // Release the frozen legacy cache's IndexedDB handle first — it stays
          // open for the tab's lifetime and would otherwise block the wipe's
          // deleteDatabase (silent on Chromium, a white-screen hang on Firefox).
          closeOfflineDatabase();
          await resetOfflineStorage({ tryFlush: false });
        } catch (err) {
          console.error("[auth-provider] reset on user-switch failed:", err);
        }
      }
      // Reset the PostHog store on this genuine same-origin user switch, the
      // same way logout does. Without it the SDK keeps the previous user's
      // distinct_id, `company` group and super-properties (identify() switches
      // distinct_id but preserves groups + super-props), so the incoming user's
      // first events + session replay ride the previous identity. Runs before
      // setLastUserId re-renders and lets the observability effect identify the
      // new user, so the new identity starts from an anonymous store.
      resetAnalytics();
      // The TanStack cache survives an in-app user switch (only Dexie is
      // wiped above) and the identity-context queries are not user-scoped.
      // Drop them so the new user's observability effect and pages can't
      // read the previous user's company/groups/employee for up to their
      // staleTime.
      queryClient.removeQueries({ queryKey: ["company", "me"] });
      queryClient.removeQueries({ queryKey: groupKeys.all });
      queryClient.removeQueries({ queryKey: employeeKeys.all });
      // Feature flags carry per-user overrides under a global key, so the new
      // user must not inherit the previous user's flags for up to staleTime.
      queryClient.removeQueries({ queryKey: featureFlagKeys.all });
      setLastUserId(effectiveUserId);
      if (!cancelled) setUserSwitchReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [isFetched, effectiveUserId, queryClient]);

  const hasPermission = (feature: string): boolean => {
    if (!user) return false;
    return (user as any).feature_permissions?.includes(feature) || false;
  };

  const hasAnyPermission = (features: string[]): boolean => {
    if (!user) return false;
    return features.some((feature) => hasPermission(feature));
  };

  const hasAllPermissions = (features: string[]): boolean => {
    if (!user) return false;
    return features.every((feature) => hasPermission(feature));
  };

  return (
    isFetched &&
    userSwitchReady && (
      <UserContext.Provider value={effectiveUser as UserResponse | null}>
        <PermissionContext.Provider
          value={{ hasPermission, hasAnyPermission, hasAllPermissions }}
        >
          {children}
        </PermissionContext.Provider>
      </UserContext.Provider>
    )
  );
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  let router = useRouter();
  let pathname = usePathname();
  let context = useContext(UserContext);
  let queryClient = useQueryClient();

  const {
    data: user,
    isFetched,
    error,
  } = useQuery({
    queryKey: ["user"],
    queryFn: api.users.getMe,
    retry: false,
    refetchInterval: getUserRefetchInterval,
  });

  useObservabilityContext(user);

  const hasPermission = (feature: string): boolean => {
    if (!user) return false;
    return (user as any).feature_permissions?.includes(feature) || false;
  };

  const hasAnyPermission = (features: string[]): boolean => {
    if (!user) return false;
    return features.some((feature) => hasPermission(feature));
  };

  const hasAllPermissions = (features: string[]): boolean => {
    if (!user) return false;
    return features.every((feature) => hasPermission(feature));
  };

  // When the bearer is missing at mount, kick to /login immediately
  // instead of waiting for a query refetch to surface a 401. Two reasons
  // we can't rely on the query path:
  //   1. TanStack Query's IndexedDB persistence + 60 s staleTime returns
  //      the cached user without firing `getMe`, so no 401 is ever
  //      observed and the error-driven redirect below never fires.
  //   2. `resetQueries`/`refetchQueries` from this hook race the
  //      `OptionalAuthProvider`'s `isFetched && userSwitchReady` gate
  //      that controls whether this provider even mounts.
  // Direct router.push sidesteps both. Covers manual devtools bearer
  // wipes too, not just the logout test.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!localStorage.getItem("bearer")) {
      const { search, hash } = window.location;
      router.push(
        "/login?next=" + encodeURIComponent(pathname + search + hash),
      );
    }
  }, [pathname, router]);

  useEffect(() => {
    if (!error) return;
    // Clear user cache to prevent stale data causing redirect loops
    queryClient.setQueryData(["user"], null);
    queryClient.removeQueries({ queryKey: ["user"] });
    setSentryUserContext(null);
    setPostHogUserContext(null, null);
    void (async () => {
      try {
        // Token is already invalid — flushing is pointless. Wipe the local
        // DB so the next user (or the same user re-authenticating) starts
        // from the server's truth. Close the legacy cache's IndexedDB handle
        // first so its open connection can't block (Chromium) or hang
        // (Firefox white screen) the wipe's deleteDatabase.
        closeOfflineDatabase();
        await resetOfflineStorage({ tryFlush: false });
      } catch (err) {
        console.error("[auth-provider] reset on auth error failed:", err);
      }
      const { search, hash } = window.location;
      router.push(
        "/login?next=" + encodeURIComponent(pathname + search + hash),
      );
    })();
  }, [error, pathname, router, queryClient]);

  if (context !== null) {
    return children;
  }

  return (
    isFetched &&
    user !== undefined && (
      <UserContext.Provider value={user as UserResponse}>
        <PermissionContext.Provider
          value={{ hasPermission, hasAnyPermission, hasAllPermissions }}
        >
          {children}
        </PermissionContext.Provider>
      </UserContext.Provider>
    )
  );
}
